import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../styles/HptScreens.css';

const API_URL = process.env.REACT_APP_API_URL;

const CreateTemplateScreen = () => {
    const navigate = useNavigate();

    // --- ESTADOS DE DATOS ---
    const [catalogo, setCatalogo] = useState([]);
    const [plantillasDisponibles, setPlantillasDisponibles] = useState([]);
    const [plantillaActiva, setPlantillaActiva] = useState(null);
    // --- ESTADOS DEL FORMULARIO ---
    const [formData, setFormData] = useState({
        nombre_plantilla: '',
        descripcion_trabajo: '',
        tipo_incidencia: 'PREVENTIVO',
        dias_previstos: 1,
        operarios_previstos: 1,
        duracion_prevista_hours: 1,
        preventivos: []
    });

    // --- ESTADOS DE MATRICES ---
    const [seleccionados, setSeleccionados] = useState([]);
    const [detallesRiesgos, setDetallesRiesgos] = useState({});
    const [materiales, setMateriales] = useState([]);
    const [recursos, setRecursos] = useState([]);

    // --- BUSCADOR DE USUARIOS/GRUPOS ---
    const [busquedaUsuarios, setBusquedaUsuarios] = useState('');
    const [mostrarDesplegableUsuarios, setMostrarDesplegableUsuarios] = useState(false);
    const [opcionesUsuarios, setOpcionesUsuarios] = useState([]);

    const CONFIG_RECURSOS = {
        'EQUIPO ESPECIAL': ['Andamiero', 'Calorifugador', 'Soldador', 'ENDs', 'Mecanizado', 'Grup. Electrógeno', 'Compresor', 'Luminarias'],
        'ACCESO': ['Andamio / taller', 'Plataforma elevadora', 'Anticaídas Retráctiles', 'Línea de vida', 'Banqueta/alfombra', 'Escalera'],
        'ENERGÍA TRABAJO': ['Agua', 'N2', 'Argon', 'Aire', 'Electricidad', 'Gasolina/Gasoil'],
        'ENERGÍA EQUIPO': ['HTF', 'Agua', 'Vapor', 'Químicos', 'Nitrógeno', 'Electrica', '75v CC/50v CA'],
        'TRABAJOS PREVIOS': ['Enclavamiento eléctrico', 'Descargo eléctrico', 'Descargo mecánico', 'Comprobación NO arranque', 'Despresurizar', 'Drenar', 'PCI Fuera de Servicio', 'Inertizar (N2)', 'Levantamiento en pruebas']
    };

    // ============================================================================
    // CARGA INICIAL
    // ============================================================================
    const fetchData = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            
            const [resCat, resPlantillas] = await Promise.all([
                axios.get(`${API_URL}/api/catalogos/seguridad`, config),
                axios.get(`${API_URL}/api/hpt/plantillas/listar`, config)
            ]);
            
            setCatalogo(resCat.data.results || resCat.data);
            setPlantillasDisponibles(resPlantillas.data || []);
        } catch (err) {
            console.error("Error cargando datos:", err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // ============================================================================
    // CARGAR PLANTILLA (MODO EDICIÓN)
    // ============================================================================
    const handleCargarPlantilla = (e) => {
        const idSeleccionado = e.target.value;

        if (!idSeleccionado || idSeleccionado === "vacio") {
            setPlantillaActiva(null);
            setSeleccionados([]);
            setRecursos([]);
            setMateriales([]);
            setDetallesRiesgos({});
            setFormData({
                nombre_plantilla: '',
                descripcion_trabajo: '',
                tipo_incidencia: 'PREVENTIVO',
                dias_previstos: 1,
                operarios_previstos: 1,
                duracion_prevista_hours: 1,
                preventivos: []
            });
            return;
        }

        const plantilla = plantillasDisponibles.find(p => p.id_plantilla === parseInt(idSeleccionado));
        if (!plantilla) return;

        setPlantillaActiva(plantilla.id_plantilla);

        const riesgos = JSON.parse(plantilla.json_riesgos || '[]');
        const materialesJson = JSON.parse(plantilla.json_materiales || '[]');
        const recursosJson = JSON.parse(plantilla.json_recursos || '[]');
        const preventivosJson = JSON.parse(plantilla.json_preventivos || '[]');

        setFormData({
            nombre_plantilla: plantilla.nombre_plantilla || '',
            descripcion_trabajo: plantilla.descripcion_trabajo || '',
            tipo_incidencia: plantilla.tipo_incidencia || 'PREVENTIVO',
            dias_previstos: plantilla.dias_previstos || 1,
            operarios_previstos: plantilla.operarios_previstos || 1,
            duracion_prevista_hours: plantilla.duracion_prevista_hours || 1,
            preventivos: preventivosJson
        });
        
        setSeleccionados(riesgos.map(r => r.id_item_seguridad));
        
        const nuevosDetallesRiesgos = {};
        riesgos.forEach(r => {
            if (r.observacion_riesgo) nuevosDetallesRiesgos[r.id_item_seguridad] = r.observacion_riesgo;
        });
        setDetallesRiesgos(nuevosDetallesRiesgos);
        setMateriales(materialesJson);
        setRecursos(recursosJson);
    };

    // ============================================================================
    // BUSCADOR DE USUARIOS PREVENTIVOS
    // ============================================================================
    const buscarUsuariosServidor = async (texto) => {
        setBusquedaUsuarios(texto);
        if (texto.length < 2) { setOpcionesUsuarios([]); return; }
        try {
            const token = localStorage.getItem('userToken');
            const res = await axios.get(`${API_URL}/api/catalogos/usuarios/buscar?q=${texto}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpcionesUsuarios(res.data.results || []);
        } catch (err) { console.error("Error buscando usuarios:", err); }
    };

    const seleccionarListaUsuarios = (opcion) => {
        const seleccion = opcion.valor;
        if (!formData.preventivos.includes(seleccion)) {
            setFormData({ ...formData, preventivos: [...formData.preventivos, seleccion] });
        }
        setBusquedaUsuarios('');
        setMostrarDesplegableUsuarios(false);
    };

    // ============================================================================
    // MANEJO DE LISTAS (RIESGOS, RECURSOS, MATERIALES)
    // ============================================================================
    const handleRecursoToggle = (cat, nombre, checked) => {
        const exists = recursos.find(r => r.categoria === cat && r.nombre_recurso === nombre);
        if (exists) {
            setRecursos(recursos.map(r => (r.categoria === cat && r.nombre_recurso === nombre) ? { ...r, valor_check: checked } : r));
        } else {
            setRecursos([...recursos, { categoria: cat, nombre_recurso: nombre, valor_check: checked, detalle_texto: "" }]);
        }
    };

    const handleRecursoText = (cat, nombre, texto) => {
        setRecursos(recursos.map(r => 
            (r.categoria === cat && r.nombre_recurso === nombre) ? { ...r, detalle_texto: texto } : r
        ));
    };

    const addMaterial = () => setMateriales([...materiales, { descripcion: '', unidades: '', almacen: '' }]);
    const handleMaterialChange = (idx, field, val) => {
        const newM = [...materiales];
        newM[idx][field] = val;
        setMateriales(newM);
    };

    // ============================================================================
    // GUARDAR, EDITAR Y BORRAR PLANTILLA
    // ============================================================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre_plantilla) return Swal.fire('Atención', 'El nombre es obligatorio.', 'warning');

        const payload = {
            ...formData,
            riesgos: seleccionados.map(id => ({ 
                id_item_seguridad: id, 
                observacion_riesgo: detallesRiesgos[id] || '' 
            })),
            materiales: materiales,
            recursos: recursos
        };

        const token = localStorage.getItem('userToken');
        const config = { headers: { Authorization: `Bearer ${token}` } };

        try {
            if (plantillaActiva) {
                // MODO EDICIÓN
                await axios.put(`${API_URL}/api/hpt/plantillas/${plantillaActiva}`, payload, config);
                Swal.fire('Actualizada', 'La plantilla se ha actualizado correctamente.', 'success');
            } else {
                // MODO CREACIÓN
                await axios.post(`${API_URL}/api/hpt/plantillas/crear`, payload, config);
                Swal.fire('Creada', 'La nueva plantilla se ha guardado correctamente.', 'success');
            }
            fetchData();
            setPlantillaActiva(null);
        } catch (error) {
            Swal.fire('Error', error.response?.data?.error || 'No se pudo guardar la plantilla', 'error');
        }
    };

    const handleDelete = async () => {
        if (!plantillaActiva) return;

        const confirm = await Swal.fire({
            title: '¿Eliminar Plantilla?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#999',
            confirmButtonText: 'Sí, eliminar'
        });

        if (confirm.isConfirmed) {
            const token = localStorage.getItem('userToken');
            try {
                await axios.delete(`${API_URL}/api/hpt/plantillas/${plantillaActiva}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Eliminada', 'La plantilla ha sido borrada.', 'success');
                setPlantillaActiva(null);
                setFormData({ ...formData, nombre_plantilla: '', descripcion_trabajo: '' });
                setSeleccionados([]);
                setRecursos([]);
                setMateriales([]);
                fetchData();
            } catch (error) {
                Swal.fire('Error', 'No se pudo eliminar la plantilla', 'error');
            }
        }
    };

    const categoriasSeguridad = [...new Set(catalogo.map(item => item.categoria))];

    return (
        <div className="container">
            <div className="top-header">
                <div className="d-flex align-center">
                    <span className="ptw-label">GESTOR DE PLANTILLAS:</span>
                    <div className="ptw-badge">
                        <span style={{ fontSize: '20px' }}>💾</span> CREADOR Y EDITOR
                    </div>
                </div>
                <div className="header-actions">
                    <button type="button" className="btn-close" onClick={() => navigate(-1)}>Volver</button>
                    {plantillaActiva && (
                        <button type="button" className="btn-reject-now" style={{ padding: '8px 15px' }} onClick={handleDelete}>
                            🗑️ Eliminar Plantilla
                        </button>
                    )}
                    <button type="button" className="btn-save" onClick={handleSubmit}>
                        {plantillaActiva ? '💾 Guardar Cambios' : '💾 Crear Nueva'}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="main-layout" style={{ maxWidth: '1000px', margin: '0 auto' }}>
                
                {/* --- SELECTOR DE EDICIÓN --- */}
                <div className="main-card mb-20" style={{ backgroundColor: '#fdfdfd', borderLeft: '4px solid #1a237e' }}>
                    <div className="template-banner" style={{ margin: 0 }}>
                        <span className="template-title">CARGAR PLANTILLA EXISTENTE (Para Editar/Borrar):</span>
                        <select className="main-select" onChange={handleCargarPlantilla} value={plantillaActiva || 'vacio'}>
                            <option value="vacio">-- ➕ Crear nueva plantilla desde cero --</option>
                            {plantillasDisponibles.map(p => (
                                <option key={p.id_plantilla} value={p.id_plantilla}>
                                    {p.nombre_plantilla}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="main-card mb-20">
                    <div className="card-header-internal">1. Datos Base de la Plantilla</div>
                    
                    <div className="row mt-10">
                        <div className="info-box flex-2">
                            <span className="info-label">Nombre de la Plantilla (Único) *</span>
                            <input required type="text" name="nombre_plantilla" value={formData.nombre_plantilla} onChange={handleChange} className="main-input" placeholder="Ej: Mantenimiento Preventivo Bombas Centrífugas..." />
                        </div>
                        <div className="info-box flex-1">
                            <span className="info-label">Tipo de Incidencia</span>
                            <select name="tipo_incidencia" value={formData.tipo_incidencia} onChange={handleChange} className="main-select">
                                <option value="PREVENTIVO">PREVENTIVO</option>
                                <option value="CORRECTIVO">CORRECTIVO</option>
                                <option value="MODIFICACION">MODIFICACIÓN</option>
                                <option value="OTROS">OTROS</option>
                            </select>
                        </div>
                    </div>

                    <div className="info-box mt-10">
                        <span className="info-label">Descripción Estandarizada del Trabajo</span>
                        <textarea rows="3" name="descripcion_trabajo" value={formData.descripcion_trabajo} onChange={handleChange} className="main-textarea" placeholder="Escriba aquí la descripción genérica..."></textarea>
                    </div>

                    <div className="row mt-10">
                        <div className="info-box flex-1">
                            <span className="info-label">Días Previstos</span>
                            <input type="number" min="1" max="15" name="dias_previstos" value={formData.dias_previstos} onChange={handleChange} className="main-input" />
                        </div>
                        <div className="info-box flex-1">
                            <span className="info-label">Operarios Previstos</span>
                            <input type="number" min="1" name="operarios_previstos" value={formData.operarios_previstos} onChange={handleChange} className="main-input" />
                        </div>
                        <div className="info-box flex-1">
                            <span className="info-label">Horas Previstas Totales</span>
                            <input type="number" min="1" name="duracion_prevista_hours" value={formData.duracion_prevista_hours} onChange={handleChange} className="main-input" />
                        </div>
                    </div>
                </div>

                {/* --- RECURSOS PREVENTIVOS --- */}
                <div className="main-card mb-20">
                    <div className="card-header-internal">2. Recurso Preventivo Estándar</div>
                    <div className="row mt-10">
                        <div className="info-box flex-1 relative"> 
                            <span className="info-label">🛡️ ASIGNAR RECURSO PREVENTIVO (Usuario o Grupo):</span>
                            <input 
                                type="text" 
                                className="main-input" 
                                placeholder="Escriba el nombre, apellidos, rol o grupo..."
                                value={busquedaUsuarios}
                                onChange={(e) => {
                                    buscarUsuariosServidor(e.target.value);
                                    setMostrarDesplegableUsuarios(true);
                                }}
                                onFocus={() => setMostrarDesplegableUsuarios(true)}
                                onBlur={() => setTimeout(() => setMostrarDesplegableUsuarios(false), 200)}
                            />

                            {mostrarDesplegableUsuarios && busquedaUsuarios.length >= 2 && (
                                <ul className="autocomplete-list">
                                    {opcionesUsuarios.length > 0 ? (
                                        opcionesUsuarios.map((op, index) => (
                                            <li key={`${op.valor}-${index}`} className="autocomplete-item" onMouseDown={() => seleccionarListaUsuarios(op)}>
                                                <strong>{op.tipo === 'GRUPO' ? '👥 Grupo:' : '👤 Usuario:'} {op.etiqueta}</strong><br/>
                                                <span className="text-small text-muted">{op.descripcion}</span>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="autocomplete-item">No se encontraron resultados...</li>
                                    )}
                                </ul>
                            )}

                            <div className="selected-tags mt-10">
                                {formData.preventivos.map((prev, index) => (
                                    <span key={index} className="skill-tag skill-preventive" style={{display: 'inline-block', margin: '5px'}}>
                                        {prev} 
                                        <button type="button" onClick={() => setFormData({...formData, preventivos: formData.preventivos.filter((_, i) => i !== index)})} style={{marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#d32f2f', fontWeight: 'bold'}}>✖</button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- MATERIALES --- */}
                <div className="main-card mb-20">
                    <div className="card-header-internal">3. Repuestos y Materiales Habituales</div>
                    <div className="mt-10">
                        {materiales.map((m, idx) => (
                            <div key={idx} className="row mt-10 align-center">
                                <input className="main-input flex-2" placeholder="Descripción" value={m.descripcion} onChange={e => handleMaterialChange(idx, 'descripcion', e.target.value)} />
                                <input className="main-input flex-1" placeholder="Uds." value={m.unidades} onChange={e => handleMaterialChange(idx, 'unidades', e.target.value)} />
                                <input className="main-input flex-1" placeholder="Almacén/Ubicación" value={m.almacen} onChange={e => handleMaterialChange(idx, 'almacen', e.target.value)} />
                                <button type="button" onClick={() => setMateriales(materiales.filter((_,i) => i !== idx))} className="btn-delete-row">✖</button>
                            </div>
                        ))}
                        <button type="button" onClick={addMaterial} className="add-btn mt-10">+ Añadir Repuesto/Material</button>
                    </div>
                </div>

                {/* --- RECURSOS Y ENERGÍAS --- */}
                <div className="main-card mb-20">
                    <div className="card-header-internal">4. Recursos y Energías Estándar</div>
                    <div className="resource-grid mt-10">
                        {Object.entries(CONFIG_RECURSOS).map(([categoria, items]) => (
                            <div key={categoria} className="res-col">
                                <div className="res-cat-title">{categoria}</div>
                                <div className="grid-checks">
                                    {items.map(item => {
                                        const recursoEncontrado = recursos.find(r => r.categoria === categoria && r.nombre_recurso === item);
                                        const isChecked = recursoEncontrado ? recursoEncontrado.valor_check : false;
                                        const textoDetalle = recursoEncontrado ? recursoEncontrado.detalle_texto : '';
                                        
                                        return (
                                            <div key={item} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                <label className={`hab-item ${isChecked ? 'checked' : ''}`}>
                                                    <input type="checkbox" checked={isChecked} onChange={(e) => handleRecursoToggle(categoria, item, e.target.checked)} />
                                                    {item}
                                                </label>
                                                {item === 'Químicos' && isChecked && (
                                                    <input 
                                                        type="text" 
                                                        className="main-input res-text-input" 
                                                        placeholder="Especifique qué químico..." 
                                                        value={textoDetalle}
                                                        onChange={(e) => handleRecursoText(categoria, item, e.target.value)}
                                                        required
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* --- RIESGOS Y MEDIDAS --- */}
                <div className="main-card mb-20">
                    <div className="card-header-internal">5. Matriz de Riesgos y Medidas</div>
                    <div className="mt-10">
                        {categoriasSeguridad.map(cat => (
                            <div key={cat} className="mb-20">
                                <div className="res-cat-title">{cat}</div>
                                <div className="grid-checks">
                                    {catalogo.filter(i => i.categoria === cat).map(item => {
                                        const isSelected = seleccionados.includes(item.id_item);
                                        let badgeClass = 'risk-badge';
                                        if (isSelected) {
                                            badgeClass += item.es_riesgo ? ' checked-risk' : ' checked-measure';
                                        }
                                        const esComodinMaquinaria = item.codigo === '10007';

                                        return (
                                            <div key={item.id_item}>
                                                <label className={badgeClass}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isSelected} 
                                                        onChange={() => {
                                                            if (isSelected) {
                                                                setSeleccionados(seleccionados.filter(i => i !== item.id_item));
                                                                const nuevosDetalles = { ...detallesRiesgos };
                                                                delete nuevosDetalles[item.id_item];
                                                                setDetallesRiesgos(nuevosDetalles);
                                                            } else {
                                                                setSeleccionados([...seleccionados, item.id_item]);
                                                            }
                                                        }} 
                                                    />
                                                    <span className={`text-small ${item.es_riesgo ? 'text-risk' : 'text-measure'}`}>
                                                        <strong>{item.codigo}</strong> - {item.descripcion}
                                                    </span>
                                                </label>
                                                {isSelected && esComodinMaquinaria && (
                                                    <input 
                                                        type="text" 
                                                        className="main-input res-text-input" 
                                                        placeholder="Especifique máquina: ej. Tractor" 
                                                        value={detallesRiesgos[item.id_item] || ''}
                                                        onChange={(e) => setDetallesRiesgos({ ...detallesRiesgos, [item.id_item]: e.target.value })}
                                                        required 
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bottom-actions mt-20 mb-40 text-right">
                    <button type="submit" className="btn-save btn-large">
                        {plantillaActiva ? '💾 Guardar Cambios' : '💾 Finalizar Plantilla Nueva'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateTemplateScreen;